import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Clock, CalendarIcon, Users, Package, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TIME_SLOTS = ['10:00', '13:00', '16:00'];
const TIME_LABELS = { '10:00': '10:00 AM', '13:00': '1:00 PM', '16:00': '4:00 PM' };

export const RescheduleModal = ({ booking, open, onClose, onSuccess }) => {
  const [packages, setPackages] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [newDate, setNewDate] = useState(null);
  const [newTime, setNewTime] = useState('');
  const [newPackageId, setNewPackageId] = useState('');
  const [newGuestCount, setNewGuestCount] = useState(0);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && booking) {
      setNewDate(null);
      setNewTime('');
      setNewPackageId(booking.package_id);
      setNewGuestCount(booking.guest_count);
      setReason('');
      fetchPackages();
    }
  }, [open, booking]);

  useEffect(() => {
    if (newDate) fetchSlots(newDate);
  }, [newDate]);

  const fetchPackages = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/packages`);
      setPackages(res.data);
    } catch (e) {}
  };

  const fetchSlots = async (date) => {
    try {
      const dateStr = date.toISOString().split('T')[0];
      const res = await axios.get(`${API_URL}/api/bookings/available-slots/${dateStr}`);
      // If same date as current booking, include the current time slot as available
      let slots = res.data.available_slots;
      if (dateStr === booking?.date && !slots.includes(booking.time_slot)) {
        slots.push(booking.time_slot);
        slots.sort();
      }
      setAvailableSlots(slots);
      setNewTime('');
    } catch (e) {}
  };

  const handleSubmit = async () => {
    const changes = {};
    if (newDate) changes.new_date = newDate.toISOString().split('T')[0];
    if (newTime) changes.new_time_slot = newTime;
    if (newPackageId && newPackageId !== booking.package_id) changes.new_package_id = newPackageId;
    if (newGuestCount && newGuestCount !== booking.guest_count) changes.new_guest_count = newGuestCount;
    if (reason) changes.reason = reason;

    if (!changes.new_date && !changes.new_time_slot && !changes.new_package_id && !changes.new_guest_count) {
      toast.error('No changes selected');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/bookings/${booking.booking_id}/reschedule`, changes, { withCredentials: true });
      toast.success('Booking rescheduled successfully!');
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reschedule');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/bookings/${booking.booking_id}/cancel`, {}, { withCredentials: true });
      toast.success('Booking cancelled');
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    } finally {
      setLoading(false);
    }
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 90);
    return date < today || date > maxDate;
  };

  const selectedPkg = packages.find(p => p.id === newPackageId);

  if (!booking) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Fredoka, sans-serif' }}>Reschedule Booking</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Booking Info */}
          <div className="bg-[#FFFDF8] rounded-xl p-4 border border-[#E8E5DF]">
            <p className="text-sm font-semibold text-[#1E1E24] mb-2">Current Booking</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span className="text-[#6B6B7A]">Date: <strong className="text-[#1E1E24]">{booking.date}</strong></span>
              <span className="text-[#6B6B7A]">Time: <strong className="text-[#1E1E24]">{TIME_LABELS[booking.time_slot]}</strong></span>
              <span className="text-[#6B6B7A]">Package: <strong className="text-[#1E1E24]">{booking.package_name}</strong></span>
              <span className="text-[#6B6B7A]">Guests: <strong className="text-[#1E1E24]">{booking.guest_count}</strong></span>
            </div>
          </div>

          {/* New Date */}
          <div>
            <Label className="text-[#1E1E24] font-semibold mb-2 flex items-center gap-2">
              <CalendarIcon className="w-4 h-4" /> New Date (optional)
            </Label>
            <Calendar
              mode="single"
              selected={newDate}
              onSelect={setNewDate}
              disabled={isDateDisabled}
              className="rounded-xl border border-[#E8E5DF] p-3"
              data-testid="reschedule-calendar"
            />
          </div>

          {/* New Time */}
          {newDate && (
            <div>
              <Label className="text-[#1E1E24] font-semibold mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4" /> New Time Slot
              </Label>
              <div className="grid grid-cols-3 gap-3">
                {TIME_SLOTS.map(slot => {
                  const available = availableSlots.includes(slot);
                  return (
                    <button key={slot} onClick={() => available && setNewTime(slot)}
                      disabled={!available}
                      className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                        newTime === slot ? 'border-[#FF3B6B] bg-[#FF3B6B]/5 text-[#FF3B6B]'
                        : available ? 'border-[#E8E5DF] hover:border-[#FF3B6B]/50 text-[#1E1E24]'
                        : 'border-[#E8E5DF] bg-gray-50 text-[#6B6B7A] cursor-not-allowed'
                      }`}
                      data-testid={`reschedule-slot-${slot}`}
                    >
                      {TIME_LABELS[slot]}
                      {!available && <span className="block text-xs">(Booked)</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* New Package */}
          <div>
            <Label className="text-[#1E1E24] font-semibold mb-2 flex items-center gap-2">
              <Package className="w-4 h-4" /> Change Package (optional)
            </Label>
            <Select value={newPackageId} onValueChange={setNewPackageId}>
              <SelectTrigger className="rounded-xl border-[#E8E5DF]" data-testid="reschedule-package">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {packages.map(pkg => (
                  <SelectItem key={pkg.id} value={pkg.id}>
                    {pkg.name} - ${pkg.price} (up to {pkg.guests} guests)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* New Guest Count */}
          <div>
            <Label className="text-[#1E1E24] font-semibold mb-2 flex items-center gap-2">
              <Users className="w-4 h-4" /> Guest Count
            </Label>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => setNewGuestCount(Math.max(1, newGuestCount - 1))}
                className="rounded-full w-10 h-10 p-0" data-testid="reschedule-guest-minus">-</Button>
              <span className="text-lg font-semibold text-[#1E1E24] w-12 text-center" data-testid="reschedule-guest-count">{newGuestCount}</span>
              <Button variant="outline" onClick={() => setNewGuestCount(Math.min(selectedPkg?.guests || 12, newGuestCount + 1))}
                className="rounded-full w-10 h-10 p-0" data-testid="reschedule-guest-plus">+</Button>
              {selectedPkg && <span className="text-sm text-[#6B6B7A]">Max {selectedPkg.guests} for {selectedPkg.name}</span>}
            </div>
          </div>

          {/* Reason */}
          <div>
            <Label className="text-[#1E1E24] font-semibold mb-2">Reason for Change (optional)</Label>
            <Textarea value={reason} onChange={e => setReason(e.target.value)}
              placeholder="e.g., Schedule conflict, want to upgrade..."
              rows={2} className="rounded-xl border-[#E8E5DF]" data-testid="reschedule-reason" />
          </div>

          {/* Price change notice */}
          {newPackageId !== booking.package_id && selectedPkg && (
            <div className="bg-[#FFD166]/10 rounded-xl p-4 border border-[#FFD166]">
              <p className="text-sm text-[#B8860B]">
                Package change: {booking.package_name} (${booking.subtotal}) → {selectedPkg.name} (${selectedPkg.price}). 
                Total will be recalculated with 13% tax.
              </p>
            </div>
          )}

          {/* 48-hour warning */}
          {booking.date && (() => {
            const partyDate = new Date(booking.date);
            const hoursUntil = (partyDate - new Date()) / (1000 * 60 * 60);
            if (hoursUntil < 48 && hoursUntil > 0) {
              return (
                <div className="bg-[#FF3B6B]/10 rounded-xl p-4 border border-[#FF3B6B]">
                  <p className="text-sm text-[#FF3B6B]">
                    Your party is within 48 hours. Only admins can reschedule at this point. Please contact us.
                  </p>
                </div>
              );
            }
            return null;
          })()}

          {/* Actions */}
          <div className="flex justify-between pt-4 border-t border-[#E8E5DF]">
            <Button variant="outline" onClick={handleCancel} disabled={loading}
              className="rounded-full border-red-300 text-red-500 hover:bg-red-50" data-testid="cancel-booking-btn">
              Cancel Booking
            </Button>
            <Button onClick={handleSubmit} disabled={loading}
              className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full btn-hover" data-testid="confirm-reschedule-btn">
              {loading ? 'Processing...' : 'Confirm Changes'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RescheduleModal;
